import $ from '../core/Dom';
import Viewport from '../core/Viewport';
import EcoMode from '../lib/EcoMode';

// Inject a global keyframes rule once. Uses a CSS variable for distance so we can vary per instance.
let keyframesInjected = false;
const ensureKeyframes = () => {
    if (keyframesInjected) return;
    const id = 'kh-overflow-scrollbar-keyframes';
    if (document.getElementById(id)) {
        keyframesInjected = true;
        return;
    }
    const style = document.createElement('style');
    style.id = id;
    style.textContent = '@keyframes kh-overflow-scrollbar-ticker { from { transform: translateX(0); } to { transform: translateX(calc(var(--ticker-shift, 0px) * -1)); } }';
    document.head.appendChild(style);
    keyframesInjected = true;
};

export default (el, props) => {
    const $el = $(el);
    const $scrollWrapper = $el.find('[data-scroll-wrapper]');
    const $scrollContent = $el.find('[data-scroll-content]');

    let lastW = 0;
    let tickerEnabled = false;
    let trackEl = null; // container we create to hold original + clone

    const getGap = () => {
        const host = $el.get(0);
        if (!host) return 0;
        const cs = window.getComputedStyle(host);
        const pl = parseFloat(cs.paddingLeft || '0') || 0;
        const pr = parseFloat(cs.paddingRight || '0') || 0;
        return Math.max(0, Math.round(pl + pr));
    };

    const needsTicker = () => {
        const wrapperW = $scrollWrapper.width();
        const contentEl = $scrollContent.get(0);
        if (!contentEl || !wrapperW) return false;
        const contentW = contentEl.scrollWidth;
        return contentW > wrapperW + 1; // allow for rounding
    };

    const clearClones = () => {
        const wrapperEl = $scrollWrapper.get(0);
        const contentEl = $scrollContent.get(0);
        if (!wrapperEl || !contentEl) return;

        // If we have created a track wrapper, remove it and restore original content
        const existingTrack = wrapperEl.querySelector('[data-ticker-track]');
        if (existingTrack) {
            const original = existingTrack.querySelector('[data-scroll-content]:not([data-clone])') || contentEl;
            // Clean up inline styles we may have applied
            if (original && original.style) {
                original.style.removeProperty('flex');
                original.style.removeProperty('margin-right');
            }
            existingTrack.remove();
            if (original && !wrapperEl.contains(original)) {
                wrapperEl.appendChild(original);
            }
            trackEl = null;
            return;
        }

        // Fallback: remove any cloned content sibling if present
        const contentClone = wrapperEl.querySelector('[data-scroll-content][data-clone]');
        if (contentClone) contentClone.remove();
        trackEl = null;
    };

    const enableTicker = () => {
        if (tickerEnabled) {
            // Recompute shift/duration in case sizes changed
            applyAnimationStyles();
            return;
        }
        
        ensureKeyframes();
        
        const wrapperEl = $scrollWrapper.get(0);
        const contentEl = $scrollContent.get(0);
        if (!wrapperEl || !contentEl) return;

        // Duplicate the entire content element to allow seamless loop
        clearClones();

        // Create a track that will be animated and hold the two copies
        const track = document.createElement('div');
        track.setAttribute('data-ticker-track', '1');
        
        // Ensure horizontal layout of the two blocks
        track.style.display = 'flex';
        track.style.flexWrap = 'nowrap';
        track.style.alignItems = 'stretch';

        // Move original content into the track
        track.appendChild(contentEl);
        
        // Prevent shrinking so widths remain exact and copies don't overlap
        if (contentEl && contentEl.style) {
            contentEl.style.flex = '0 0 auto';
        }

        // Create and append the clone of the entire content element
        const clone = contentEl.cloneNode(true);
        clone.setAttribute('data-clone', '1');
        
        // Also prevent clone from shrinking
        if (clone && clone.style) {
            clone.style.flex = '0 0 auto';
        }
        
        track.appendChild(clone);

        // Insert the track into the wrapper
        wrapperEl.appendChild(track);
        trackEl = track;

        applyAnimationStyles();
        tickerEnabled = true;
    };

    const disableTicker = () => {
        if (!tickerEnabled) {
            // Ensure styles are clean even if not marked enabled
            removeAnimationStyles();
            clearClones();
            return;
        }
        removeAnimationStyles();
        clearClones();
        tickerEnabled = false;
    };

    const getSingleRunWidth = () => {
        const contentEl = $scrollContent.get(0);
        if (!contentEl) return 0;
        // Width of a single full run equals the scrollWidth of the original content element plus the inter-copy gap
        const base = contentEl.scrollWidth || 0;
        const gap = trackEl ? getGap() : 0;
        return Math.ceil(base + gap);
    };

    const removeAnimationStyles = () => {
        const target = trackEl || $scrollContent.get(0);
        if (!target) return;
        target.style.removeProperty('--ticker-shift');
        target.style.animation = '';
        target.style.willChange = '';
    };

    const applyAnimationStyles = () => {
        const target = trackEl || $scrollContent.get(0);
        if (!target) return;
        const shift = getSingleRunWidth();
        if (!shift) return;

        // Apply inter-copy spacing equal to outer wrapper's horizontal padding
        if (trackEl) {
            const original = trackEl.querySelector('[data-scroll-content]:not([data-clone])');
            if (original && original.style) {
                original.style.marginRight = getGap() + 'px';
            }
        }

        // pixels per second speed
        const pps = 100; // adjust if needed
        const duration = Math.max(10, Math.round((shift / pps) * 100) / 100); // min 10s for readability
        target.style.setProperty('--ticker-shift', shift + 'px');
        target.style.willChange = 'transform';
        target.style.animation = 'kh-overflow-scrollbar-ticker ' + duration + 's linear infinite';
    };

    const update = () => {
        // Respect reduced motion
        if (EcoMode.reduceMotion()) {
            disableTicker();
            return;
        }
        
        if (needsTicker()) {
            enableTicker();
        } else {
            disableTicker();
        }
    };

    const init = () => {
        // initialize and bind
        update();
        Viewport.on('resize', onResize);
    };

    const destroy = () => {
        Viewport.off('resize', onResize);
        disableTicker();
    };

    const onResize = () => {
        if (lastW !== Viewport.width) {
            lastW = Viewport.width;
            
            update();
        }
    };

    return {
        init,
        destroy
    };

};
